import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { PasskeyService } from '../../services/passkey.service';
import { IdentifyResult } from '../../models/sso.models';

/**
 * Schritt 1 des Identifier-First-Login-Flows.
 *
 * Sammelt die Email-Adresse und fragt das Backend, ob die Domain einem
 * SSO-IdP zugeordnet ist. Emittiert das Ergebnis hoch zur LoginPage,
 * die je nach Match Schritt 2 oder den SSO-Redirect-State zeigt.
 *
 * Hat keinen Passwort-Eingang — das passiert erst in Schritt 2.
 */
@Component({
  selector: 'app-login-identify-step',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
  template: `
    <div class="t2d-login__form-header">
      <h2 class="t2d-login__form-title">Anmelden</h2>
    </div>

    @if (errorMessage()) {
      <div class="t2d-login__error-banner" role="alert" aria-live="polite">
        <svg class="t2d-login__error-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9a1 1 0 10-2 0v4a1 1 0 102 0V9zm-1-4a1 1 0 100 2 1 1 0 000-2z" clip-rule="evenodd" />
        </svg>
        {{ errorMessage() }}
      </div>
    }

    <form [formGroup]="form" (ngSubmit)="submit()" class="t2d-login__form" novalidate autocomplete="on">
      <div class="t2d-login__field"
           [class.t2d-login__field--error]="form.controls.email.touched && form.controls.email.invalid">
        <label class="t2d-login__label" for="login-email">Email-Adresse</label>
        <div class="t2d-login__input-wrapper">
          <input
            id="login-email"
            type="email"
            autocomplete="email"
            formControlName="email"
            class="t2d-login__input"
            placeholder="name@firma.de"
          />
          <svg class="t2d-login__input-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
          </svg>
        </div>
        @if (form.controls.email.touched && form.controls.email.invalid) {
          <span class="t2d-login__field-error" role="alert">
            Bitte eine gültige Email-Adresse eingeben.
          </span>
        }
      </div>

      <button
        type="submit"
        class="t2d-login__submit"
        [disabled]="form.invalid || isLoading()"
      >
        @if (isLoading()) { Wird geprüft… } @else { Weiter }
      </button>

      <div class="t2d-login__divider">alternativ</div>

      <button
        type="button"
        class="t2d-login__btn-outline"
        [disabled]="isPasskeyBusy()"
        (click)="onPasskeyClick()"
      >
        @if (isPasskeyBusy()) { Authentifiziere… } @else { 🔑 Mit Passkey anmelden }
      </button>
    </form>
  `,
})
export class LoginIdentifyStepComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly passkeyService = inject(PasskeyService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() set email(value: string) {
    if (value) {
      this.form.patchValue({ email: value });
    }
  }
  @Output() identified = new EventEmitter<IdentifyResult>();

  protected readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  readonly isLoading = signal(false);
  readonly isPasskeyBusy = signal(false);
  readonly errorMessage = signal<string | null>(null);

  submit(): void {
    if (this.form.invalid || this.isLoading()) return;
    const email = (this.form.value.email ?? '').trim();
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.authService
      .identify(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.isLoading.set(false);
          this.identified.emit(result);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.errorMessage.set(this.decodeError(err));
        },
      });
  }

  async onPasskeyClick(): Promise<void> {
    this.isPasskeyBusy.set(true);
    this.errorMessage.set(null);
    try {
      await this.passkeyService.login();
      // Cookie ist gesetzt; Page-Reload triggert app.routes.ts /dashboard.
      window.location.href = '/dashboard';
    } catch (err) {
      this.isPasskeyBusy.set(false);
      const message = err instanceof Error ? err.message : 'Vorgang abgebrochen';
      this.errorMessage.set('Passkey-Login fehlgeschlagen: ' + message);
    }
  }

  private decodeError(err: HttpErrorResponse): string {
    if (err.status === 429) {
      const retryAfter = Number(err.headers?.get('Retry-After'));
      if (Number.isFinite(retryAfter) && retryAfter > 0) {
        const minuten = Math.ceil(retryAfter / 60);
        return `Zu viele Login-Versuche. Bitte ${minuten} Minute${minuten === 1 ? '' : 'n'} warten.`;
      }
      return 'Zu viele Login-Versuche. Bitte später erneut versuchen.';
    }
    if (err.status === 0) return 'Server nicht erreichbar. Bitte Verbindung prüfen.';
    if (err.status >= 500) return 'Server-Fehler. Bitte später erneut versuchen.';
    return 'Anmeldung konnte nicht geprüft werden.';
  }
}
